import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getContratParToken,
  getContratsASigner,
  ouvrirSignatureContrat,
  signerContrat,
  telechargerPdfContrat,
  urlPdfContrat,
} from '../services/contratService';
import { formatDate, formatDateTime, formatMontant } from './devisUtils';
import SelecteurSignature from './SelecteurSignature';
import SignaturePad from './SignaturePad';
import EnteteSignature, { ApercuDocument } from './EnteteSignature';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Clients.css';
import './Signature.css';

/** Colonnes du sélecteur de contrats à faire signer (cf. SelecteurSignature). */
const COLONNES_CONTRAT = [
  {
    cle: 'numero',
    label: 'NUMÉRO',
    rendu: (ct) => (
      <>
        <div className="devis-numero">{ct.numero}</div>
        <div className="devis-contact-email">du {formatDate(ct.date_contrat)}</div>
      </>
    ),
  },
  {
    cle: 'client',
    label: 'CLIENT',
    rendu: (ct) => (
      <>
        <div className="devis-contact-name">{ct.societe_nom || '—'}</div>
        <div className="devis-contact-email">
          {[ct.prenom, ct.nom].filter(Boolean).join(' ')} · {ct.contact_email}
        </div>
      </>
    ),
  },
  {
    cle: null,
    label: 'FORMATION',
    rendu: (ct) => (
      <>
        <div className="signature-liste-intitule">{ct.intitule_action || '—'}</div>
        <div className="devis-contact-email">{ct.dates_session_texte || '—'}</div>
      </>
    ),
  },
  {
    cle: 'montant',
    label: 'MONTANT',
    rendu: (ct) => (
      <>
        <div className="devis-montant-ht">{formatMontant(ct.montant_ht)} HT</div>
        <div className="devis-contact-email">{formatMontant(ct.montant_ttc)} TTC</div>
      </>
    ),
  },
  {
    cle: 'signature',
    label: 'SIGNATURE',
    rendu: (ct) =>
      ct.signature_client_le ? (
        <>
          <span className="devis-status-pill devis-pill-valide">Signé</span>
          <div className="devis-status-detail">le {formatDate(ct.signature_client_le)}</div>
        </>
      ) : (
        <>
          <span className="devis-status-pill devis-pill-attente">À signer</span>
          <div className="devis-status-detail">
            {ct.date_envoi ? 'contrat déjà envoyé' : 'pas encore envoyé'}
          </div>
        </>
      ),
  },
];

/**
 * Page accessible depuis la barre latérale et depuis le lien reçu par email
 * (une fois le contrat signé par l'organisme de formation) pour que le
 * client le relise puis le signe à son tour. Même principe que Signature.js
 * pour le devis, sélecteur interne compris : sans jeton, la page liste les
 * contrats que l'organisme a signés et ouvre celui qu'un client présent
 * doit contresigner, sans envoi d'email. Le contrat d'abord, tel qu'il a
 * été établi — c'est lui que le client lit —, puis la signature.
 */
function SignatureContrat() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  // Cf. Signature.js : seul l'opérateur venu du sélecteur a un retour.
  const retourListe = state?.retourListe || null;
  const [contrat, setContrat] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(false);
  const [erreurSignature, setErreurSignature] = useState(null);
  const [telechargement, setTelechargement] = useState(false);

  useEffect(() => {
    if (!token) return;
    let annule = false;

    setLoading(true);
    setError(null);

    getContratParToken(token)
      .then((res) => {
        if (!annule) setContrat(res.data);
      })
      .catch((err) => {
        if (!annule) setError(err.message || 'Contrat introuvable.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [token]);

  async function validerSignature(imageDataUrl) {
    setValidation(true);
    setErreurSignature(null);
    try {
      const res = await signerContrat(token, imageDataUrl);
      setContrat(res.data);
    } catch (err) {
      setErreurSignature(err.message || 'La signature n’a pas pu être enregistrée.');
    } finally {
      setValidation(false);
    }
  }

  async function telechargerPdf() {
    setTelechargement(true);
    try {
      await telechargerPdfContrat(contrat.id, { forcer: true, token });
    } catch (err) {
      setErreurSignature(err.message || 'Téléchargement impossible.');
    } finally {
      setTelechargement(false);
    }
  }

  const signe = Boolean(contrat?.signature_client_le);
  const urlApercu = contrat ? urlPdfContrat(contrat.id, { apercu: true, token, forcer: signe }) : null;

  return (
    <div className="devis-page devis-page-scroll signature-doc">
      {token ? (
        <EnteteSignature
          titre={contrat ? `Contrat ${contrat.numero}` : 'Signature du contrat'}
          sousTitre={
            contrat
              ? `${contrat.societe_nom || ''}${contrat.date_contrat ? ` · du ${formatDate(contrat.date_contrat)}` : ''}`
              : null
          }
          retour={retourListe ? () => navigate(retourListe) : null}
        />
      ) : (
        <div className="devis-header">
          <div>
            <h1 className="devis-title">Contrat</h1>
            {/* Cf. Signature.js : sans contrat chargé, on est en interne. */}
            <p className="devis-subtitle">
              Choisissez le contrat à faire signer par un client présent.
            </p>
          </div>
        </div>
      )}

      {!token && (
        <SelecteurSignature
          charger={getContratsASigner}
          colonnes={COLONNES_CONTRAT}
          placeholder="Rechercher un numéro, un client..."
          compter={(n) => `${n} contrat${n > 1 ? 's' : ''}`}
          estSigne={(ct) => Boolean(ct.signature_client_le)}
          vide={{
            titre: 'Aucun contrat à faire signer sur cette période.',
            sousTitre:
              'Un contrat apparaît ici dès qu’il est établi.',
          }}
          onChoisir={async (ct) => {
            const res = await ouvrirSignatureContrat(ct.id);
            navigate(`/signature-contrat/${res.data.token}`, {
              state: { retourListe: '/signature-contrat' },
            });
          }}
        />
      )}
      {token && loading && <div className="signature-etat">Chargement du contrat...</div>}
      {token && !loading && error && (
        <div className="signature-etat signature-etat-erreur">{error}</div>
      )}

      {token && !loading && !error && contrat && (
        <>
          <ApercuDocument url={urlApercu} titre={`Contrat ${contrat.numero}`} />

          {signe && (
            <div className="signature-etat signature-etat-succes">
              <p>Contrat signé le {formatDateTime(contrat.signature_client_le)}.</p>
              <div className="signature-etat-succes-actions">
                <button
                  type="button"
                  className="devis-btn"
                  onClick={telechargerPdf}
                  disabled={telechargement}
                >
                  {telechargement ? 'Téléchargement' : 'Télécharger le PDF signé'}
                </button>
              </div>
            </div>
          )}

          {/* ---------------- Signer ---------------- */}
          {(
            <div className="signature-section">
              <div className="devis-form-section-title">Signature</div>
              {signe ? (
                <div className="signature-etat">
                  Ce contrat est signé. Vous pouvez le télécharger ci-dessus.
                </div>
              ) : (
                <>
                  <p className="signature-mention">
                    En signant ci-dessous (précédé de «&nbsp;lu et approuvé&nbsp;»), vous acceptez
                    les termes du présent contrat pour le compte de{' '}
                    <strong>{contrat.societe_nom || 'votre entreprise'}</strong>.
                  </p>
                  {erreurSignature && (
                    <div className="signature-etat signature-etat-erreur">{erreurSignature}</div>
                  )}
                  <SignaturePad onValider={validerSignature} validation={validation} />
                </>
              )}
            </div>
          )}

        </>
      )}
    </div>
  );
}

export default SignatureContrat;
